/**
 * CarePlanForm — controlled form for create/edit of a care plan.
 *
 * Phase 3 (Care Plan v2) extraction. Currently surfaces the legacy v1 fields
 * (title, review_due_date, medical_alerts, personal_care_needs, important_notes)
 * to preserve byte-identical UX with the inline form that lived inside
 * ParticipantDetailPage.js. v2 enhancements (linked goals/risks/restrictive
 * practices/medications + structured sections) layer on top of this via the
 * CarePlanV2Sections sibling component when `care_plan_v2_enabled` is ON.
 *
 * Phase 5: when `care_plan_ai_assist_enabled` is ON each text field gets an
 * inline "Suggest with AI" button. The AI returns a DRAFT only; user accepts
 * via AIDraftReviewDialog before any value is written back to the field.
 */
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { TrinitiDatePicker } from '@/components/triniti-datetime';
import { AISuggestButton } from '@/components/ai/AISuggestButton';
import { AIDraftReviewDialog } from '@/components/ai/AIDraftReviewDialog';

function flattenDraft(d) {
  // Turn the AI care_plan_section draft into a single-line value suitable
  // for the legacy v1 flat fields.
  if (!d) return '';
  const bullets = Array.isArray(d.bullets) ? d.bullets.join(' · ') : '';
  return [d.summary, bullets].filter(Boolean).join(' — ');
}

function SectionRow({ sectionKey, sectionLabel, value, onChange, testid, participantId }) {
  const [draft, setDraft] = useState(null);
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <Label className="text-xs">{sectionLabel}</Label>
        <AISuggestButton
          feature="care_plan_section"
          testid={`ai-suggest-section-${sectionKey}`}
          buildPayload={async () => ({
            participant_id: participantId,
            section_key: sectionKey,
            participant_ctx: { participant_id: participantId },
            user_brief: value || `Suggest content for the "${sectionLabel}" section of this Care Plan.`,
          })}
          onDraft={(env) => setDraft(env)}
        />
      </div>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 text-sm"
        data-testid={testid}
      />
      <AIDraftReviewDialog
        open={!!draft}
        onOpenChange={(v) => !v && setDraft(null)}
        draftEnvelope={draft}
        participantId={participantId}
        targetEntity={`care_plan.${sectionKey}`}
        onAccept={(d) => onChange(flattenDraft(d))}
      />
    </div>
  );
}

export function CarePlanForm({ value, onChange, onSubmit, onCancel, v2Enabled, v2Slot, participantId }) {
  return (
    <Card data-testid="care-plan-form">
      <CardContent className="p-4">
        <form onSubmit={onSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Title</Label>
              <Input
                value={value.title}
                onChange={(e) => onChange({ ...value, title: e.target.value })}
                required
                className="h-8 text-sm"
                data-testid="care-plan-title-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Review Due</Label>
              <TrinitiDatePicker
                value={value.review_due_date}
                onChange={(v) => onChange({ ...value, review_due_date: v })}
                className="h-8 text-sm"
                testId="care-plan-review-due-input"
              />
            </div>
          </div>
          <SectionRow
            sectionKey="medical_alerts" sectionLabel="Medical Alerts"
            value={value.medical_alerts || ''}
            onChange={(v) => onChange({ ...value, medical_alerts: v })}
            testid="care-plan-medical-alerts-input"
            participantId={participantId}
          />
          <SectionRow
            sectionKey="personal_care_needs" sectionLabel="Personal Care Needs"
            value={value.personal_care_needs || ''}
            onChange={(v) => onChange({ ...value, personal_care_needs: v })}
            testid="care-plan-personal-care-input"
            participantId={participantId}
          />
          <SectionRow
            sectionKey="important_notes" sectionLabel="Important Notes"
            value={value.important_notes || ''}
            onChange={(v) => onChange({ ...value, important_notes: v })}
            testid="care-plan-important-notes-input"
            participantId={participantId}
          />
          {/* v2 Sections / Linked entities slot — rendered by parent when flag ON */}
          {v2Enabled && v2Slot}
          <div className="flex gap-2">
            <Button size="sm" type="submit" data-testid="care-plan-create-submit">Create</Button>
            <Button size="sm" variant="ghost" type="button" onClick={onCancel}>Cancel</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
